import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { timesheetreportService } from '../../services/timesheetreportsService';
import { loader } from '../../compnents/loadermd';
import { FaCalendarAlt, FaClock, FaChartBar, FaHourglassHalf, FaCalendarCheck, FaInfoCircle } from 'react-icons/fa';

// ---------- SVG helpers ----------
function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutSlicePath(cx, cy, rOuter, rInner, startAngle, endAngle) {
    if (endAngle - startAngle >= 360) endAngle = startAngle + 359.99;
    const s1 = polarToCartesian(cx, cy, rOuter, startAngle);
    const e1 = polarToCartesian(cx, cy, rOuter, endAngle);
    const s2 = polarToCartesian(cx, cy, rInner, endAngle);
    const e2 = polarToCartesian(cx, cy, rInner, startAngle);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    return [
        `M ${s1.x.toFixed(2)} ${s1.y.toFixed(2)}`,
        `A ${rOuter} ${rOuter} 0 ${large} 1 ${e1.x.toFixed(2)} ${e1.y.toFixed(2)}`,
        `L ${s2.x.toFixed(2)} ${s2.y.toFixed(2)}`,
        `A ${rInner} ${rInner} 0 ${large} 0 ${e2.x.toFixed(2)} ${e2.y.toFixed(2)}`,
        'Z'
    ].join(' ');
}

function DonutChart({ full, half }) {
    const cx = 60, cy = 60, rOuter = 50, rInner = 30;
    const total = (full + half) || 1;
    const fullDeg = (full / total) * 360;
    return (
        <svg width="120" height="120" viewBox="0 0 120 120">
            <circle cx={cx} cy={cy} r={rOuter} fill="none" stroke="#f3f4f6" strokeWidth="20" />
            {full > 0 && (
                <path d={donutSlicePath(cx, cy, rOuter, rInner, 0, fullDeg)} fill="#3b82f6" />
            )}
            {half > 0 && (
                <path d={donutSlicePath(cx, cy, rOuter, rInner, fullDeg, fullDeg + (half / total) * 360)} fill="#f59e0b" />
            )}
        </svg>
    );
}

function WeeklyBarChart({ data, monthLabel }) {
    const W = 320, H = 130;
    const padL = 38, padR = 14, padT = 12, padB = 30;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const maxY = 20;
    const ySteps = [0, 5, 10, 15, 20];
    const n = data.length;
    const slot = chartW / n;
    const barW = slot * 0.45;

    const barX = (i) => padL + i * slot + (slot - barW) / 2;
    const yPos = (v) => padT + chartH - (v / maxY) * chartH;
    const linePoints = data.map((d, i) => `${(barX(i) + barW / 2).toFixed(1)},${yPos(d.billedHours).toFixed(1)}`).join(' ');

    return (
        <div style={{ overflowX: 'auto' }}>
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ minWidth: '220px', maxWidth: '420px' }}>
                {ySteps.map(v => (
                    <g key={v}>
                        <line x1={padL} y1={yPos(v)} x2={W - padR} y2={yPos(v)} stroke="#e5e7eb" strokeWidth="1" />
                        <text x={padL - 6} y={yPos(v) + 4} textAnchor="end" fontSize="11" fill="#9ca3af">{v}</text>
                    </g>
                ))}
                <text x="13" y={padT + chartH / 2} textAnchor="middle" fontSize="11" fill="#9ca3af"
                    transform={`rotate(-90, 13, ${padT + chartH / 2})`}>Hours</text>
                {data.map((d, i) => (
                    <rect key={i} x={barX(i)} y={yPos(d.workingHours)} width={barW}
                        height={((d.workingHours / maxY) * chartH).toFixed(1)}
                        fill="#3b82f6" rx="3" />
                ))}
                <polyline points={linePoints} fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinejoin="round" />
                {data.map((d, i) => (
                    <circle key={i} cx={barX(i) + barW / 2} cy={yPos(d.billedHours)} r="4"
                        fill="#fff" stroke="#22c55e" strokeWidth="2" />
                ))}
                <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="#e5e7eb" strokeWidth="1" />
                {data.map((d, i) => (
                    <text key={i} x={barX(i) + barW / 2} y={H - padB + 16} textAnchor="middle" fontSize="11" fill="#6b7280">{d.week}</text>
                ))}
            </svg>
        </div>
    );
}

// ---------- Helpers ----------
function fmtMinutes(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function monthLabel(monthStr) {
    if (!monthStr) return '';
    return new Date(monthStr + '-02').toLocaleString('default', { month: 'long', year: 'numeric' });
}

// ---------- Main component ----------
const DeepAnalysisReport = ({ tabname }) => {
    const toast = useRef(null);
    const userdata = localStorage.getItem('user') !== '' ? JSON.parse(localStorage.getItem('user')) : {};

    const [empList, setEmpList] = useState([]);
    const [formData, setFormData] = useState({ employee: '', employeeName: '', month: '' });
    const [reportData, setReportData] = useState(null);
    const [isloaded, setLoading] = useState(true);

    useEffect(() => { getemplist(); }, []);

    function getemplist() {
        timesheetreportService.getemployeelist({ managerid: userdata.id }).then((response) => {
            if (response?.data?.data) setEmpList(response.data.data);
        }).catch(() => {});
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'employee') {
            const name_ = e.target.options[e.target.selectedIndex]?.text || '';
            setFormData(prev => ({ ...prev, employee: value, employeeName: value ? name_ : '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleShow = () => {
        if (!formData.employee || !formData.month) {
            toast.current.show({ severity: 'warn', summary: 'Validation', detail: 'Please select an employee and month', life: 3000 });
            return;
        }
        setLoading(false);
        setReportData(null);

        // TODO: replace setTimeout block with real API call, e.g.:
        // adminService.getdeepanalysis({ userid: formData.employee, month: formData.month })
        //   .then(res => { setReportData(res.data.data); setLoading(true); })
        //   .catch(() => { toast.current.show({...}); setLoading(true); });

        setTimeout(() => {
            setReportData({
                totalWorkingDays: 21,
                avgDailyMinutes: 405,
                fullDays: 18,
                halfDays: 3,
                totalMinutes: 10110,
                weeklyData: [
                    { week: 'Week 1', workingHours: 16.2, billedHours: 15.5 },
                    { week: 'Week 2', workingHours: 15.8, billedHours: 13.0 },
                    { week: 'Week 3', workingHours: 17.1, billedHours: 15.2 },
                    { week: 'Week 4', workingHours: 16.0, billedHours: 13.5 },
                ],
                targetHours: 160,
                targetMetDays: 18,
                attendanceLog: [
                    { date: 'May 24', clockIn: '08:58 AM', clockOut: '05:32 PM', totalMin: 514, status: 'Full Day' },
                    { date: 'May 23', clockIn: '09:02 AM', clockOut: '05:45 PM', totalMin: 523, status: 'Full Day' },
                    { date: 'May 22', clockIn: '09:02 AM', clockOut: '05:45 PM', totalMin: 310, status: 'Half Day' },
                    { date: 'May 21', clockIn: '08:58 AM', clockOut: '05:32 PM', totalMin: 508, status: 'Full Day' },
                    { date: 'May 20', clockIn: '08:02 AM', clockOut: '05:45 PM', totalMin: 495, status: 'Full Day' },
                ],
            });
            setLoading(true);
        }, 600);
    };

    const card = (children, extraStyle = {}) => (
        <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            padding: '18px 20px',
            height: '100%',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05), 0 0 1px rgba(0,0,0,0.04)',
            transition: 'box-shadow 0.2s ease',
            ...extraStyle
        }}>
            {children}
        </div>
    );

    const sectionLabel = (text) => (
        <div style={{
            fontSize: '10.5px',
            fontWeight: 700,
            color: '#b0b7c3',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '10px',
            marginTop: '8px',
            paddingLeft: '2px',
        }}>
            {text}
        </div>
    );

    const targetPct = reportData
        ? Math.min(Math.round((reportData.totalMinutes / (reportData.targetHours * 60)) * 100), 100)
        : 0;
    const targetMet = reportData && reportData.totalMinutes >= reportData.targetHours * 60;

    return (
        <>
            <Toast ref={toast} />
            <div className="card-style-padding">

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px', padding: '12px 16px', background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.05), 0 0 1px rgba(0,0,0,0.04)' }}>
                    <h6 style={{ fontWeight: 700, color: '#1a1f3a', margin: 0 }}>{tabname}</h6>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>User:</span>
                            <select name="employee" className="form-control" style={{ width: '180px', height: '34px', fontSize: '13px', padding: '4px 8px' }}
                                value={formData.employee} onChange={handleChange}>
                                <option value="">Select Employee</option>
                                {empList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                        </div>
                        <input type="month" name="month" className="form-control"
                            style={{ width: '150px', height: '34px', fontSize: '13px', padding: '4px 8px' }}
                            value={formData.month} onChange={handleChange} />
                        <Button label="Show" icon="pi pi-search" severity="success" rounded
                            onClick={handleShow} loading={!isloaded}
                            style={{ height: '34px', padding: '0 16px', fontSize: '13px' }} />
                    </div>
                </div>

                {!isloaded && (
                    <div style={{ textAlign: 'center', padding: '30px 0' }}>{loader.Loadermd()}</div>
                )}

                {reportData && isloaded && (
                    <>
                        {/* ── Section 1 ── */}
                        {sectionLabel('SECTION 1: KEY PERFORMANCE METRICS')}
                        <div className="row" style={{ marginBottom: '20px' }}>

                            {/* Card 1: Working Days */}
                            <div className="col-md-3 col-sm-6" style={{ marginBottom: '12px' }}>
                                <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', padding: '16px 18px', height: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
                                        <div style={{ background: '#fff7ed', borderRadius: '10px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FaCalendarAlt style={{ color: '#f59e0b', fontSize: '22px' }} />
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500, marginBottom: '4px' }}>Working Days</div>
                                            <div style={{ fontSize: '26px', fontWeight: 800, color: '#1a1f3a', lineHeight: 1 }}>
                                                {reportData.totalWorkingDays}
                                                <span style={{ fontSize: '14px', fontWeight: 600, marginLeft: '4px', color: '#374151' }}>Days</span>
                                            </div>
                                        </div>
                                    </div>
                                    <hr style={{ margin: '0 0 10px 0', borderColor: '#f3f4f6', borderTop: '1px solid #f3f4f6' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaCalendarCheck style={{ color: '#9ca3af', fontSize: '12px' }} />
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>Monthly total</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Avg Daily Minutes */}
                            <div className="col-md-3 col-sm-6" style={{ marginBottom: '12px' }}>
                                <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', padding: '16px 18px', height: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
                                        <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FaClock style={{ color: '#22c55e', fontSize: '22px' }} />
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500, marginBottom: '4px' }}>Avg. Daily Minutes</div>
                                            <div style={{ fontSize: '26px', fontWeight: 800, color: '#1a1f3a', lineHeight: 1 }}>
                                                {reportData.avgDailyMinutes}
                                                <span style={{ fontSize: '14px', fontWeight: 600, marginLeft: '4px', color: '#374151' }}>min.</span>
                                            </div>
                                        </div>
                                    </div>
                                    <hr style={{ margin: '0 0 10px 0', borderColor: '#f3f4f6', borderTop: '1px solid #f3f4f6' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaCalendarAlt style={{ color: '#9ca3af', fontSize: '12px' }} />
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>Per working day</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 3: Full vs Half Days */}
                            <div className="col-md-3 col-sm-6" style={{ marginBottom: '12px' }}>
                                <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', padding: '16px 18px', height: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
                                        <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FaChartBar style={{ color: '#ef4444', fontSize: '22px' }} />
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500, marginBottom: '4px' }}>Full vs. Half Days</div>
                                            <div style={{ fontSize: '26px', fontWeight: 800, color: '#1a1f3a', lineHeight: 1 }}>
                                                {reportData.fullDays}
                                                <span style={{ fontSize: '14px', fontWeight: 600, marginLeft: '4px', color: '#374151' }}>Full</span>
                                            </div>
                                        </div>
                                    </div>
                                    <hr style={{ margin: '0 0 10px 0', borderColor: '#f3f4f6', borderTop: '1px solid #f3f4f6' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{reportData.halfDays} Half Days this month</span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 4: Total Hours */}
                            <div className="col-md-3 col-sm-6" style={{ marginBottom: '12px' }}>
                                <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', padding: '16px 18px', height: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
                                        <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FaHourglassHalf style={{ color: '#3b82f6', fontSize: '22px' }} />
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500, marginBottom: '4px' }}>Total Working Hours</div>
                                            <div style={{ fontSize: '26px', fontWeight: 800, color: '#1a1f3a', lineHeight: 1 }}>
                                                {fmtMinutes(reportData.totalMinutes)}
                                            </div>
                                        </div>
                                    </div>
                                    <hr style={{ margin: '0 0 10px 0', borderColor: '#f3f4f6', borderTop: '1px solid #f3f4f6' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaInfoCircle style={{ color: '#9ca3af', fontSize: '12px' }} />
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>Cumulative this month</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Section 2 + Attendance Log (same row) ── */}
                        {sectionLabel('SECTION 2: HOURLY ANALYSIS')}
                        <div className="row" style={{ marginBottom: '20px' }}>

                            {/* Chart */}
                            <div className="col-md-6" style={{ marginBottom: '12px' }}>
                                {card(
                                    <>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#1a1f3a', marginBottom: '14px' }}>
                                            Weekly Working vs. Billed Hours ({monthLabel(formData.month)})
                                        </div>
                                        <WeeklyBarChart data={reportData.weeklyData} />
                                        <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                <span style={{ width: '14px', height: '14px', background: '#3b82f6', display: 'inline-block', borderRadius: '3px' }} />
                                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Working Hours</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                <span style={{ width: '22px', height: '2.5px', background: '#22c55e', display: 'inline-block' }} />
                                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Billed Hours</span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Attendance Log */}
                            <div className="col-md-6" style={{ marginBottom: '12px' }}>
                                {card(
                                    <>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#1a1f3a', marginBottom: '16px' }}>Recent Daily Attendance Log</div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                <thead>
                                                    <tr style={{ background: '#f9fafb' }}>
                                                        {['Date', 'Clock In', 'Clock Out', 'Total Min', 'Status', 'Details'].map(h => (
                                                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.attendanceLog.map((row, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                            <td style={{ padding: '8px 10px', color: '#374151' }}>{row.date}</td>
                                                            <td style={{ padding: '8px 10px', color: '#374151' }}>{row.clockIn}</td>
                                                            <td style={{ padding: '8px 10px', color: '#374151' }}>{row.clockOut}</td>
                                                            <td style={{ padding: '8px 10px', color: '#374151' }}>{row.totalMin}</td>
                                                            <td style={{ padding: '8px 10px' }}>
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    padding: '2px 10px',
                                                                    borderRadius: '999px',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    background: row.status === 'Full Day' ? '#dcfce7' : '#fef3c7',
                                                                    color: row.status === 'Full Day' ? '#16a34a' : '#d97706',
                                                                }}>
                                                                    {row.status}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '8px 10px' }}>
                                                                <span style={{ color: '#3b82f6', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>View</span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ── Section 3: Target Progress ── */}
                        {sectionLabel('SECTION 3: TARGET TRACKING')}
                        <div className="row">

                            {/* Target Progress */}
                            <div className="col-md-12" style={{ marginBottom: '12px' }}>
                                {card(
                                    <>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#1a1f3a', marginBottom: '16px' }}>Monthly Target Progress</div>

                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Working Target</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                {fmtMinutes(reportData.totalMinutes)} / {reportData.targetHours}h Target
                                            </span>
                                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#1a1f3a' }}>{targetPct}%</span>
                                        </div>
                                        <div style={{ background: '#e5e7eb', borderRadius: '999px', height: '9px', overflow: 'hidden', marginBottom: '4px' }}>
                                            <div style={{ width: `${targetPct}%`, background: '#3b82f6', height: '100%', borderRadius: '999px', transition: 'width 0.6s ease' }} />
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                                            {targetMet && (
                                                <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: 600 }}>✓ Target Met</span>
                                            )}
                                        </div>

                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>Target Summary</div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                            <thead>
                                                <tr style={{ background: '#f9fafb' }}>
                                                    {['Monthly Target', 'Current Total', 'Target Met Days', 'Remaining Target'].map(h => (
                                                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '8px 10px', color: '#374151' }}>{reportData.targetHours}h</td>
                                                    <td style={{ padding: '8px 10px', color: '#374151' }}>{fmtMinutes(reportData.totalMinutes)}</td>
                                                    <td style={{ padding: '8px 10px', color: '#374151' }}>{reportData.targetMetDays}</td>
                                                    <td style={{ padding: '8px 10px', color: targetMet ? '#22c55e' : '#374151', fontWeight: targetMet ? 600 : 400 }}>
                                                        {targetMet ? '0h (Target achieved)' : fmtMinutes(reportData.targetHours * 60 - reportData.totalMinutes)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {!reportData && isloaded && (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#b0b7c3', fontSize: '14px', background: '#fff', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        Select an employee and month, then click <strong>Show</strong> to view the analysis.
                    </div>
                )}
            </div>
        </>
    );
};

export default DeepAnalysisReport;
